import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { disposeNtsVehicle } from "../lib/api";
import { Coins, Trash2, Gift, Wrench } from "lucide-react";
import PrintReleaseFormButton from "./PrintReleaseFormButton";
import { salvageShort, salvageFull, tenantCurrency } from "../lib/company";

/**
 * Dialog for marking an NTS-owned vehicle as disposed.
 *
 * Methods:
 *  - `sold`      → buyer name + sale amount required (drives the reconciliation report)
 *  - `scrapped`  → optional notes (e.g. "scrap yard - Tasman Metals")
 *  - `donated`   → optional recipient in notes
 *  - `kept`      → moved to NTS internal fleet, no $ recovery
 *
 * The reconciliation between original `released_to_nts_amount_owed` (bad debt
 * we wrote off) and `sold_amount` happens in the parent table — this dialog
 * is purely about capturing the disposal event.
 */
export default function DisposeNtsDialog({ open, onOpenChange, job, onDisposed }) {
  const [method, setMethod] = useState("sold");
  const [soldTo, setSoldTo] = useState("");
  const [soldAmount, setSoldAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      // Reset on each open so a previous record's data doesn't bleed across.
      setMethod("sold");
      setSoldTo("");
      setSoldAmount("");
      setNotes("");
    }
  }, [open]);

  const submit = async () => {
    if (!job) return;
    // Client-side guard mirrors the server validation so the user gets
    // immediate feedback before we hit the network.
    if (method === "sold") {
      if (!soldTo.trim()) { toast.error("Buyer name is required"); return; }
      const amt = parseFloat(soldAmount);
      if (Number.isNaN(amt) || amt < 0) { toast.error(`Enter a valid sale amount (${tenantCurrency()})`); return; }
    }
    setSaving(true);
    try {
      const payload = {
        method,
        sold_to: method === "sold" ? soldTo.trim() : null,
        sold_amount: method === "sold" ? parseFloat(soldAmount) : null,
        notes: notes.trim() || null,
      };
      const updated = await disposeNtsVehicle(job.id, payload);
      onDisposed?.(updated);
      const label =
        method === "sold" ? `Sold to ${soldTo.trim()} for $${parseFloat(soldAmount).toFixed(2)}`
        : method === "scrapped" ? "Marked as scrapped"
        : method === "donated" ? "Marked as donated"
        : `Kept in ${salvageShort()} fleet`;
      toast.success(label);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record disposal");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="nts-dispose-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">
            Mark Vehicle Disposed
          </DialogTitle>
          <DialogDescription>
            {job?.vehicle ? <strong className="text-slate-800 dark:text-slate-100">{job.vehicle}</strong> : "Vehicle"}
            {job?.rego && <> · <span className="font-mono">{job.rego}</span></>}
            <br />
            <span className="text-xs text-slate-500">
              Original debt: ${(job?.released_to_nts_amount_owed || 0).toFixed(2)}
              {` — capture how this vehicle left ${salvageShort()} for reconciliation.`}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="nts-dispose-method" className="uppercase-label">Disposal Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger id="nts-dispose-method" data-testid="nts-dispose-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sold" data-testid="nts-dispose-method-sold">
                  <span className="inline-flex items-center gap-2"><Coins className="h-4 w-4 text-emerald-600" /> Sold (recovers $$$)</span>
                </SelectItem>
                <SelectItem value="scrapped" data-testid="nts-dispose-method-scrapped">
                  <span className="inline-flex items-center gap-2"><Trash2 className="h-4 w-4 text-slate-500" /> Scrapped (no recovery)</span>
                </SelectItem>
                <SelectItem value="donated" data-testid="nts-dispose-method-donated">
                  <span className="inline-flex items-center gap-2"><Gift className="h-4 w-4 text-rose-500" /> Donated</span>
                </SelectItem>
                <SelectItem value="kept" data-testid="nts-dispose-method-kept">
                  <span className="inline-flex items-center gap-2"><Wrench className="h-4 w-4 text-blue-500" /> Kept in {salvageShort()} fleet</span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {method === "sold" && (
            <>
              <div>
                <Label htmlFor="nts-dispose-soldto" className="uppercase-label">Sold To (buyer name)</Label>
                <Input
                  id="nts-dispose-soldto"
                  value={soldTo}
                  onChange={(e) => setSoldTo(e.target.value)}
                  placeholder="e.g. local auto wreckers"
                  data-testid="nts-dispose-soldto"
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="nts-dispose-amount" className="uppercase-label">Sale Amount ({tenantCurrency()})</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <Input
                    id="nts-dispose-amount"
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    value={soldAmount}
                    onChange={(e) => setSoldAmount(e.target.value)}
                    placeholder="0.00"
                    className="pl-7"
                    data-testid="nts-dispose-amount"
                  />
                </div>
                {soldAmount && job?.released_to_nts_amount_owed != null && (() => {
                  const a = parseFloat(soldAmount);
                  if (Number.isNaN(a)) return null;
                  const debt = job.released_to_nts_amount_owed;
                  const net = a - debt;
                  return (
                    <div className={`text-xs mt-1 font-semibold ${net >= 0 ? "text-emerald-700 dark:text-emerald-300" : "text-rose-700 dark:text-rose-300"}`}>
                      Net vs original debt: {net >= 0 ? "+" : ""}${net.toFixed(2)} {net >= 0 ? "(profit)" : "(loss)"}
                    </div>
                  );
                })()}
              </div>
            </>
          )}

          <div>
            <Label htmlFor="nts-dispose-notes" className="uppercase-label">Notes (optional)</Label>
            <Textarea
              id="nts-dispose-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any extra context — buyer contact, condition, etc."
              data-testid="nts-dispose-notes"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          {method !== "kept" && (
            <PrintReleaseFormButton
              jobId={job?.id}
              action="disposed"
              label="Print Release Form"
              testId="nts-dispose-print-form"
              className="mr-auto"
            />
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
            data-testid="nts-dispose-submit"
          >
            {saving ? "Saving…" : "Record Disposal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
